/**
 * Helpers for rendering extracted document data fields.
 *
 * The backend stores extracted values as a flat Record<string, string> with
 * keys like "system:referenceDate", "system:vendor", "physician" etc.
 * This module provides:
 *   - fieldLabel(key, locale)  → human-readable label, with i18n lookup first
 *   - fieldType(key)           → 'date' | 'amount' | 'text'
 *
 * Field type drives the input widget rendered in ExtractedDataEditor.
 */

/** Supported field types for input rendering. */
export type ExtractedFieldType = 'date' | 'amount' | 'text';

// ---------------------------------------------------------------------------
// i18n key map — mirrors entries in locales/de.json + en.json under
// "extracted_fields". Keys are the exact extracted-value keys the backend
// stores (system: namespace as-is).
// ---------------------------------------------------------------------------
const I18N_KEY_MAP: Record<string, string> = {
  'system:referenceDate': 'extracted_fields.system_reference_date',
  'system:vendor': 'extracted_fields.system_vendor',
  'system:person': 'extracted_fields.system_person',
  'system:amount': 'extracted_fields.system_amount',
  referenceDate: 'extracted_fields.reference_date',
  documentDate: 'extracted_fields.document_date',
  physician: 'extracted_fields.physician',
  provider: 'extracted_fields.provider',
  treatmentDate: 'extracted_fields.treatment_date',
  prescriptionDate: 'extracted_fields.prescription_date',
  insurer: 'extracted_fields.insurer',
  policyNumber: 'extracted_fields.policy_number',
  subject: 'extracted_fields.subject',
  invoiceNumber: 'extracted_fields.invoice_number',
  customerNumber: 'extracted_fields.customer_number',
  billingPeriod: 'extracted_fields.billing_period',
  billingDate: 'extracted_fields.billing_date',
  organization: 'extracted_fields.organization',
  issuer: 'extracted_fields.issuer',
  medication: 'extracted_fields.medication',
  licensePlate: 'extracted_fields.license_plate',
  coverageType: 'extracted_fields.coverage_type',
};

/**
 * Returns the i18n message key for the given extracted-value key, or null
 * when no mapping exists (caller should fall back to camelCase label).
 */
export function fieldI18nKey(key: string): string | null {
  return I18N_KEY_MAP[key] ?? null;
}

// ---------------------------------------------------------------------------
// Fallback label: strip "system:" prefix, split camelCase, Title-Case
// ---------------------------------------------------------------------------
function camelCaseToTitle(s: string): string {
  // Insert space before uppercase letters that follow a lowercase letter
  return s.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

/**
 * Returns the display label for a given key.
 *
 * @param key    The raw extracted-value key (e.g. "system:referenceDate")
 * @param t      The svelte-i18n translation function `$_` (with locale baked in)
 */
export function fieldLabel(key: string, t: (k: string) => string): string {
  const i18nKey = I18N_KEY_MAP[key];
  if (i18nKey) {
    const translated = t(i18nKey);
    // svelte-i18n returns the key itself when a translation is missing — treat
    // that as a missing translation and fall back.
    if (translated !== i18nKey) {
      return translated;
    }
  }
  // Fallback: strip "system:" prefix, camelCase → Title Case
  const bare = key.startsWith('system:') ? key.slice('system:'.length) : key;
  return camelCaseToTitle(bare);
}

// ---------------------------------------------------------------------------
// Field type detection
// ---------------------------------------------------------------------------
const DATE_KEYS = new Set([
  'system:referenceDate',
  'referenceDate',
  'documentDate',
  'treatmentDate',
  'prescriptionDate',
  'billingDate',
]);

const DATE_SUFFIX_RE = /Date$/i;

/**
 * Returns the field type for rendering:
 * - 'date'   → DateInput widget (ISO YYYY-MM-DD)
 * - 'amount' → read-only display (structured "value=X,currency=Y,period=Z")
 * - 'text'   → plain text input
 */
export function fieldType(key: string): ExtractedFieldType {
  if (key === 'system:amount') return 'amount';
  if (DATE_KEYS.has(key) || DATE_SUFFIX_RE.test(key)) return 'date';
  return 'text';
}

// ---------------------------------------------------------------------------
// Ordered core fields (always shown first when present)
// ---------------------------------------------------------------------------
export const CORE_FIELD_ORDER = [
  'system:referenceDate',
  'system:vendor',
  'system:person',
  'system:amount',
] as const;

/**
 * Fields that should never be shown to the user (Spec 065 E6/S13:
 * system:warningFlags is a system diagnosis, not user data — it is
 * rendered separately as the AI-warning banner instead).
 */
export const HIDDEN_FIELDS = new Set(['system:vendorSlug', 'system:warningFlags']);

// ---------------------------------------------------------------------------
// Amount display helper
// ---------------------------------------------------------------------------

/**
 * Parses the backend amount format "value=X,currency=Y,period=Z" into parts.
 * Returns null if the format is not recognised.
 *
 * NOTE: system:amount is intentionally read-only in the editor — the
 * structured format would be silently destroyed by free-text editing.
 * A dedicated amount editor is a future enhancement.
 */
export function parseAmount(
  raw: string,
): { value: string; currency: string; period: string } | null {
  if (!raw) return null;
  const parts: Record<string, string> = {};
  for (const segment of raw.split(',')) {
    const eqIdx = segment.indexOf('=');
    if (eqIdx === -1) continue;
    parts[segment.slice(0, eqIdx).trim()] = segment.slice(eqIdx + 1).trim();
  }
  if (!('value' in parts)) return null;
  return {
    value: parts['value'] ?? '',
    currency: parts['currency'] ?? '',
    period: parts['period'] ?? '',
  };
}
